// De lagenkaart (Verkennen ronde 4, A3/A4/A5): één doek, vier lagen.
//
//   laag 1  thema's als bollen (grootte = punten, ring = groei, lijn = gedeelde
//           vaardigheden)
//   laag 2  de onderwerpen van één thema, zelfde taal
//   laag 3  de punten van één onderwerp op hun eigen plek (kaart_x/kaart_y),
//           verbonden door "vraagt om"; lijnen alleen rond wat je aanwijst
//   laag 4  één punt in het midden, buren in ringen (vraagt om binnen, hoort
//           bij midden), bronnen als vierkantjes buiten; klik een buur en die
//           komt in het midden; klik een lijn en het verband opent
//   alles   de hele kaart van ronde 1–3 (detailniveau volgt de zoom)
//   matrix  2×2 impact × moeite (vaardigheden) of impact × urgentie
//
// Wat je ziet verandert per laag; de camera, het slepen en het scrollen zijn op
// elke laag hetzelfde. Klikken gaat dieper, Esc en het kruimelpad (in de
// pagina) gaan terug. Geen bibliotheek: dezelfde canvas-aanpak als Graaf.tsx.
//
// Vorm uit de dummy: lichte ondergrond #fafafa, rand #ececec, radius 12, Inter,
// CHART-palet voor de thema's.

import { useEffect, useRef, useState } from "react";

import type { GraafBron, GraafKaartResponse, GraafPunt } from "../contracts";
import { chart, chartSeries, color, font } from "../tokens";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import {
  beweging as bewegingVan, bollen as bouwBollen, burenVan, venster as vensterVan,
  type Afbakening, type Beweging, type Bol, type Buur, type Opbouw, type Periode,
} from "../lib/verkennen";

export type Laag = 1 | 2 | 3 | 4;
export type Weergave = "lagen" | "alles" | "matrix" | "matrix2";
export type KleurModus = "soort" | "beweging" | "impact" | "urgentie" | "uitgever";
export type GrootteModus = "bronnen" | "impact" | "gelijk";

export const SOORT_KLEUR = { trend: chart.teal, vaardigheid: chart.amber, onderwerp: chart.purple } as const;
export const UITGEVER_KLEUR: Record<string, string> = {
  beleid: chart.blue, onderzoek: chart.purple, arbeidsmarktdata: chart.teal, bedrijf: chart.amber,
  media: "#ec4899", onderwijs: chart.green, regionaal: "#b45309", gemengd: chart.slate,
};
const NIEUW = chart.red;
const OUD = chart.slate;
const STIL = "#cbd5e1";

export type Stuur = {
  pas: () => void;
  zoom: (factor: number) => void;
  vliegNaar: (id: string, minZoom?: number) => void;
};

export type Telling = { punten: number; lijnen: number; zoom: number; niveau: string };

/** Methode v3: draagt dit punt (een trend) de bewijslaag "bevestigd"? Zonder
 *  het veld `bewijs` (leeslaag v1) is dat nooit zo en tekent de kaart als voorheen. */
export function isBevestigd(p: Pick<GraafPunt, "bewijs"> | null | undefined): boolean {
  return p?.bewijs?.laag === "bevestigd";
}

function alpha(hex: string, a: number): string {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function meng(a: string, b: string, t: number): string {
  const lees = (h: string) => { const n = parseInt(h.replace("#", ""), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
  const [pr, pg, pb] = lees(a); const [qr, qg, qb] = lees(b);
  const mix = (x: number, y: number) => Math.round(x + (y - x) * t);
  return `rgb(${mix(pr!, qr!)},${mix(pg!, qg!)},${mix(pb!, qb!)})`;
}

/** Kleur per thema uit het CHART-palet, in vaste volgorde. */
export function themaKleur(themas: readonly { naam: string }[], naam: string): string {
  const i = themas.findIndex((t) => t.naam === naam);
  return i < 0 ? chart.slate : (chartSeries[i % chartSeries.length] ?? chart.slate);
}

export function Lagenkaart({
  data,
  ob,
  staat,
  afbakening,
  periode,
  weergave,
  kleurModus,
  grootteModus,
  gekozen,
  markering,
  bronnen,
  vermoed,
  alleenBevestigd = false,
  hoogte = 620,
  onThema,
  onOnderwerp,
  onKies,
  onVerband,
  onAlinea,
  onTelling,
  onGebied,
  gebiedModus,
  stuur,
}: {
  data: GraafKaartResponse;
  ob: Opbouw;
  staat: Uint8Array;
  afbakening: Afbakening;
  periode: Periode | null;
  weergave: Weergave;
  kleurModus: KleurModus;
  grootteModus: GrootteModus;
  gekozen: string | null;
  markering: ReadonlySet<string> | null;
  /** de bronnen van het gekozen punt (laag 4, bronnenring) */
  bronnen: readonly GraafBron[] | null;
  /** ook de AI-vermoedens (verwant) tekenen */
  vermoed: boolean;
  /** methode v3: alles wat niet "bevestigd" is dimt naar 0,35 */
  alleenBevestigd?: boolean;
  hoogte?: number;
  onThema: (naam: string) => void;
  onOnderwerp: (id: string) => void;
  onKies: (id: string | null) => void;
  onVerband: (van: string, naar: string) => void;
  onAlinea: (alineaId: string, citaat?: string | null) => void;
  onTelling: (t: Telling) => void;
  onGebied?: (ids: string[]) => void;
  gebiedModus?: boolean;
  stuur?: { current: Stuur | null };
}) {
  const doekRef = useRef<HTMLCanvasElement | null>(null);
  const camRef = useRef({ x: 0.5, y: 0.5, z: 1 });
  const laagRef = useRef<string>("");
  const [zweef, setZweef] = useState<{ tekst: string; sub: string; x: number; y: number } | null>(null);
  const zweefRef = useRef<string | null>(null);

  const laag: Laag = gekozen ? 4 : afbakening.onderwerp ? 3 : afbakening.thema ? 2 : 1;

  useEffect(() => {
    const doek = doekRef.current;
    if (!doek) return;
    const ctx = doek.getContext("2d");
    if (!ctx) return; // jsdom: geen context, geen tekenwerk

    const punten = ob.punten;
    const lijnen = data.lijnen;
    const v = vensterVan(periode, ob.nu);
    const bewCache = new Map<number, Beweging>();
    const bew = (i: number) => {
      let b = bewCache.get(i);
      if (!b) { b = bewegingVan(punten[i]!, v); bewCache.set(i, b); }
      return b;
    };

    // ── wat er op deze laag getekend wordt ─────────────────────────────
    type Knoop = { id: string; x: number; y: number; r: number; kleur: string; naam: string; soort: "thema" | "onderwerp" | "punt" | "bron"; i?: number; bol?: Bol; bron?: GraafBron; ring?: "groei" | "krimp" | "opkomend" | null; dim?: boolean; opkomendOnderwerp?: boolean };
    type Streep = { a: Knoop; b: Knoop; dikte: number; kleur: string; streep: number[] | null; lijn?: number };
    const knopen: Knoop[] = [];
    const strepen: Streep[] = [];
    let niveau = "";

    const puntKleur = (p: GraafPunt, i: number) => {
      if (kleurModus === "beweging") { const b = bew(i); return b === "opkomend" ? NIEUW : b === "in beweging" ? chart.blue : b === "stil" ? STIL : OUD; }
      if (kleurModus === "uitgever") return UITGEVER_KLEUR[p.uitgever ?? ""] ?? color.disabled;
      if (kleurModus === "impact" || kleurModus === "urgentie") {
        const w = kleurModus === "impact" ? p.impact : p.urgentie;
        return w == null ? OUD : meng(OUD, NIEUW, Math.max(0, Math.min(1, (w - 5) / 5)));
      }
      return SOORT_KLEUR[p.soort];
    };
    const puntStraal = (p: GraafPunt) => {
      const basis = grootteModus === "bronnen" ? Math.sqrt(p.bronnen ?? 0) * 1.25 : grootteModus === "impact" ? (p.impact ?? 0) / 3.2 : 1.6;
      return 1.7 + basis + (p.isSuper ? 1.4 : 0);
    };

    const gekozenIdx = gekozen ? ob.index.get(gekozen) ?? -1 : -1;

    if (weergave === "matrix" || weergave === "matrix2") {
      niveau = weergave === "matrix" ? "2×2 impact × moeite" : "2×2 impact × urgentie";
    } else if (weergave === "alles") {
      niveau = "de hele kaart";
      const d = camRef.current.z < 1.7 ? 3 : camRef.current.z < 3.2 ? 2 : 0;
      for (let i = 0; i < punten.length; i++) {
        const p = punten[i]!;
        if (!staat[i]) continue;
        if (p.soort !== "onderwerp" && d > 0 && (p.bronnen ?? 0) < d && !p.isSuper && i !== gekozenIdx) continue;
        knopen.push({ id: p.id, x: p.x, y: p.y, r: p.soort === "onderwerp" ? 4 + Math.min(10, Math.sqrt(p.leden ?? 1) * 0.9) : puntStraal(p), kleur: p.soort === "onderwerp" ? SOORT_KLEUR.onderwerp : puntKleur(p, i), naam: p.naam, soort: p.soort === "onderwerp" ? "onderwerp" : "punt", i, dim: staat[i] === 1, opkomendOnderwerp: p.soort === "onderwerp" && p.opkomend === true, ring: p.soort !== "onderwerp" && bew(i) === "opkomend" ? "opkomend" : null });
      }
    } else if (laag === 1 || laag === 2) {
      const { bollen: bl, verbindingen } = bouwBollen(ob, staat, bew, laag === 1 ? null : afbakening.thema);
      niveau = laag === 1 ? "thema's" : `onderwerpen in ${afbakening.thema}`;
      const max = Math.max(1, ...bl.map((b) => b.punten));
      const perId = new Map<string, Knoop>();
      // bollen die elkaar niet overlappen: simpele afstoting in een paar rondes
      const pos = bl.map((b) => ({ x: b.x, y: b.y, r: 0.03 + 0.11 * Math.sqrt(b.punten / max) }));
      for (let ronde = 0; ronde < 60; ronde++) {
        for (let a = 0; a < pos.length; a++) for (let c = a + 1; c < pos.length; c++) {
          const pa = pos[a]!; const pc = pos[c]!;
          const dx = pc.x - pa.x; const dy = pc.y - pa.y;
          const d = Math.hypot(dx, dy) || 0.001;
          const min = pa.r + pc.r + 0.02;
          if (d < min) { const f = (min - d) / d * 0.5; pa.x -= dx * f; pa.y -= dy * f; pc.x += dx * f; pc.y += dy * f; }
        }
        for (const p of pos) { p.x = Math.max(p.r, Math.min(1 - p.r, p.x)); p.y = Math.max(p.r, Math.min(1 - p.r, p.y)); }
      }
      bl.forEach((b, n) => {
        const p = pos[n]!;
        const k: Knoop = { id: b.id, x: p.x, y: p.y, r: p.r, kleur: laag === 1 ? themaKleur(ob.themas, b.naam) : themaKleur(ob.themas, b.thema), naam: b.naam, soort: laag === 1 ? "thema" : "onderwerp", bol: b, ring: b.groei > 0 ? "groei" : b.groei < 0 ? "krimp" : null };
        knopen.push(k); perId.set(b.id, k);
      });
      const maxG = Math.max(1, ...verbindingen.map((x) => x.gedeeld));
      for (const vb of verbindingen.slice(0, laag === 1 ? 14 : 24)) {
        const a = perId.get(vb.a); const b = perId.get(vb.b);
        if (a && b) strepen.push({ a, b, dikte: 1 + 6 * (vb.gedeeld / maxG), kleur: chart.purple, streep: null });
      }
    } else if (laag === 3) {
      niveau = `punten in ${punten[ob.index.get(afbakening.onderwerp!) ?? -1]?.naam ?? "onderwerp"}`;
      for (const i of ob.leden.get(afbakening.onderwerp!) ?? []) {
        if (!staat[i]) continue;
        const p = punten[i]!;
        knopen.push({ id: p.id, x: p.x, y: p.y, r: puntStraal(p), kleur: puntKleur(p, i), naam: p.naam, soort: "punt", i, dim: staat[i] === 1, ring: bew(i) === "opkomend" ? "opkomend" : null });
      }
      const perIdx = new Map<number, Knoop>();
      for (const k of knopen) if (k.i !== undefined) perIdx.set(k.i, k);
      for (let li = 0; li < lijnen.length; li++) {
        const l = lijnen[li]!;
        if (l.soort !== "bron") continue;
        const a = perIdx.get(l.van); const b = perIdx.get(l.naar);
        if (a && b) strepen.push({ a, b, dikte: 0.6 + 0.5 * (l.sterkte ?? 1), kleur: chart.teal, streep: l.trede === "zelfde_onderwerp_andere_bron" ? [3, 3] : null, lijn: li });
      }
    } else {
      // laag 4: het gekozen punt in het midden, buren in ringen, bronnen buiten
      const mi = gekozenIdx;
      const mp = punten[mi];
      if (mp) {
        niveau = `rond ${mp.naam}`;
        const midden: Knoop = { id: mp.id, x: 0.5, y: 0.5, r: 9, kleur: puntKleur(mp, mi), naam: mp.naam, soort: "punt", i: mi };
        knopen.push(midden);
        const buren = burenVan(ob, lijnen, mi, staat, bew).filter((b) => vermoed || b.soort !== "verwant");
        const binnen = buren.filter((b) => b.soort === "vraagt" || b.soort === "gevraagd");
        const midring = buren.filter((b) => b.soort !== "vraagt" && b.soort !== "gevraagd");
        const zet = (lijst: Buur[], straal: number, start: number) => {
          const n = lijst.length;
          lijst.forEach((b, k) => {
            const hoek = start + (k / Math.max(1, n)) * Math.PI * 2;
            const q = punten[b.i]!;
            const kn: Knoop = { id: q.id, x: 0.5 + Math.cos(hoek) * straal, y: 0.5 + Math.sin(hoek) * straal, r: Math.max(3, puntStraal(q)), kleur: puntKleur(q, b.i), naam: q.naam, soort: "punt", i: b.i, dim: b.buiten, ring: b.opkomend ? "opkomend" : null };
            knopen.push(kn);
            const kl = b.soort === "vraagt" || b.soort === "gevraagd" ? chart.teal : b.soort === "verwant" ? chart.slate : chart.amber;
            strepen.push({ a: midden, b: kn, dikte: 0.8 + 0.7 * b.sterkte, kleur: kl, streep: b.soort === "overlapt" ? [4, 3] : b.soort === "verwant" ? [1, 3] : null, lijn: b.lijn });
          });
        };
        zet(binnen, Math.min(0.2, 0.08 + binnen.length * 0.006), -Math.PI / 2);
        zet(midring, Math.min(0.34, 0.22 + midring.length * 0.004), -Math.PI / 2 + 0.3);
        // bronnenring: vierkantjes, kleur = bronsoort, nieuwste bovenaan
        const brs = bronnen ?? [];
        brs.forEach((b, k) => {
          const hoek = -Math.PI / 2 + (k / Math.max(1, brs.length)) * Math.PI * 2;
          knopen.push({ id: `bron:${b.documentId}`, x: 0.5 + Math.cos(hoek) * 0.43, y: 0.5 + Math.sin(hoek) * 0.43, r: 5, kleur: UITGEVER_KLEUR[b.uitgeverType ?? ""] ?? color.disabled, naam: b.titel ?? b.uitgever ?? "bron", soort: "bron", bron: b });
        });
      }
    }

    // ── camera en tekenen ─────────────────────────────────────────────
    let B = 0; let H = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const meet = () => {
      const r = doek.getBoundingClientRect();
      B = r.width; H = r.height;
      doek.width = Math.round(B * dpr); doek.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const schaal = () => Math.min(B, H) * 0.9 * camRef.current.z;
    const sx = (wx: number) => (wx - camRef.current.x) * schaal() + B / 2;
    const sy = (wy: number) => (wy - camRef.current.y) * schaal() + H / 2;

    // nieuwe laag: camera passend maken, en één keer (niet bij elke render)
    const laagSleutel = `${weergave}|${laag}|${afbakening.thema}|${afbakening.onderwerp}|${gekozen}`;
    const nieuweLaag = laagRef.current !== laagSleutel;
    laagRef.current = laagSleutel;

    const gemarkeerd = markering ? new Set([...markering]) : null;
    // Trendlaag aan: geen keurmerk "bevestigd", dus geen groene ring.
    const laagAan = trendlaagAan();

    const teken = () => {
      ctx.clearRect(0, 0, B, H);
      if (weergave === "matrix" || weergave === "matrix2") { tekenMatrix(); return; }
      const z = camRef.current.z;
      // strepen (op laag 3: alleen rond het aangewezen punt, anders alle in dun)
      const zweefId = zweefRef.current;
      for (const s of strepen) {
        // laag 3: lijnen alleen rond het punt dat je aanwijst (G2) — een wolk van
        // 450 lijnen over 266 punten verdeelt niets
        if (laag === 3 && (!zweefId || (s.a.id !== zweefId && s.b.id !== zweefId))) continue;
        const a = 0.75;
        ctx.beginPath(); ctx.moveTo(sx(s.a.x), sy(s.a.y)); ctx.lineTo(sx(s.b.x), sy(s.b.y));
        ctx.strokeStyle = alpha(s.kleur, laag <= 2 ? 0.45 : a);
        ctx.lineWidth = Math.min(7, s.dikte * (laag <= 2 ? 1 : 1)); ctx.setLineDash(s.streep ?? []); ctx.stroke(); ctx.setLineDash([]);
      }
      // ringen op laag 4
      if (laag === 4 && weergave === "lagen") {
        ctx.setLineDash([2, 4]); ctx.strokeStyle = color.border; ctx.lineWidth = 1;
        for (const r of [0.2, 0.34, 0.43]) { ctx.beginPath(); ctx.arc(sx(0.5), sy(0.5), r * schaal(), 0, 6.2832); ctx.stroke(); }
        ctx.setLineDash([]);
        ctx.font = `500 10px ${font}`; ctx.fillStyle = color.faint; ctx.textAlign = "left";
        ctx.fillText("vraagt om / gevraagd door", sx(0.5) + 6, sy(0.5) - 0.2 * schaal() - 4);
        ctx.fillText("hoort bij", sx(0.5) + 6, sy(0.5) - 0.34 * schaal() - 4);
        ctx.fillText("bronnen", sx(0.5) + 6, sy(0.5) - 0.43 * schaal() - 4);
      }
      // knopen
      const labels: [number, number, number, string, number, string, boolean][] = [];
      for (const k of knopen) {
        const x = sx(k.x); const y = sy(k.y);
        if (x < -60 || x > B + 60 || y < -60 || y > H + 60) continue;
        const r = k.soort === "thema" || (k.soort === "onderwerp" && laag === 2) ? k.r * schaal() : k.r;
        let mee = k.dim ? 0.28 : 1;
        if (gemarkeerd && k.soort === "punt" && !gemarkeerd.has(k.id)) mee = Math.min(mee, 0.22);
        // methode v3: een bevestigde trend draagt een groene ring; het filter
        // "alleen bevestigd" dimt de rest
        const bev = !laagAan && k.soort === "punt" && k.i !== undefined && isBevestigd(punten[k.i]);
        // (op de hele kaart dimmen de onderwerpbollen mee; op laag 1 en 2 staan alleen bollen)
        if (alleenBevestigd && !bev && (k.soort === "punt" || (k.soort === "onderwerp" && weergave === "alles"))) mee = Math.min(mee, 0.35);
        ctx.globalAlpha = mee;
        if (k.soort === "bron") {
          ctx.fillStyle = k.kleur; ctx.fillRect(x - 5, y - 5, 10, 10);
          ctx.strokeStyle = color.surface; ctx.lineWidth = 1; ctx.strokeRect(x - 5, y - 5, 10, 10);
        } else {
          ctx.beginPath(); ctx.arc(x, y, k.id === gekozen ? r + 3 : r, 0, 6.2832);
          ctx.fillStyle = k.soort === "thema" || (k.soort === "onderwerp" && laag === 2) ? alpha(k.kleur, 0.82) : k.kleur; ctx.fill();
        }
        if (k.ring === "groei" || k.ring === "krimp") {
          ctx.lineWidth = 3; ctx.strokeStyle = k.ring === "groei" ? NIEUW : STIL; ctx.setLineDash(k.ring === "krimp" ? [3, 4] : []);
          ctx.beginPath(); ctx.arc(x, y, r + 4, 0, 6.2832); ctx.stroke(); ctx.setLineDash([]);
        } else if (k.ring === "opkomend" && kleurModus !== "beweging" && mee > 0.5) {
          ctx.lineWidth = 1.3; ctx.strokeStyle = alpha(NIEUW, 0.85); ctx.beginPath(); ctx.arc(x, y, r + 2.6, 0, 6.2832); ctx.stroke();
        }
        if (bev) {
          const rr = (k.id === gekozen ? r + 3 : r) + (k.ring === "opkomend" ? 5.2 : 2.8);
          ctx.lineWidth = 2; ctx.strokeStyle = color.accentGreen; ctx.beginPath(); ctx.arc(x, y, rr, 0, 6.2832); ctx.stroke();
        }
        if (k.soort === "onderwerp" && weergave === "alles") {
          // Een opkomend onderwerp (levensloop, owner 06-10) krijgt een stippelrand: nog geen vast onderwerp.
          ctx.setLineDash(k.opkomendOnderwerp ? [3, 3] : []);
          ctx.lineWidth = 1.4; ctx.strokeStyle = alpha(SOORT_KLEUR.onderwerp, 0.45); ctx.beginPath(); ctx.arc(x, y, r + 4, 0, 6.2832); ctx.stroke();
          ctx.setLineDash([]);
        }
        if (k.id === gekozen || k.id === zweefRef.current) {
          ctx.lineWidth = 2; ctx.strokeStyle = color.ink; ctx.beginPath(); ctx.arc(x, y, r + 3.5, 0, 6.2832); ctx.stroke();
        }
        if (mee > 0.5) {
          const isBol = k.soort === "thema" || (k.soort === "onderwerp" && laag === 2);
          let gew = isBol ? 1000 + (k.bol?.punten ?? 0) : k.soort === "bron" ? 5 : (punten[k.i ?? -1]?.bronnen ?? 0) * 10;
          if (k.id === gekozen) gew = 3000; if (k.id === zweefRef.current) gew = 2500;
          if (laag === 4) gew += 400;
          if (isBol || laag === 4 || gew > 20 || r * 1.6 > 9 || z > 4 || (gemarkeerd && gemarkeerd.has(k.id)))
            labels.push([x, y, r, isBol ? `${k.naam}` : k.naam, gew, isBol ? k.kleur : color.body, isBol]);
        }
      }
      ctx.globalAlpha = 1;
      // labels: zwaarste eerst, de rest wijkt; bollen dragen hun getal erin
      labels.sort((a, b) => b[4] - a[4]);
      const bezet: [number, number, number][] = [];
      ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
      for (const L of labels) {
        if (bezet.length >= 90) break;
        const isBol = L[6];
        ctx.font = `${isBol ? 600 : 500} ${isBol ? 13 : 11}px ${font}`;
        const tekst = L[3].length > 44 ? `${L[3].slice(0, 42)}…` : L[3];
        const br = ctx.measureText(tekst).width;
        const lx = L[0]; const ly = isBol ? L[1] + 4 : L[1] - L[2] - 5;
        if (!isBol && bezet.some((q) => Math.abs(lx - q[0]) < (br + q[2]) / 2 + 7 && Math.abs(ly - q[1]) < 14)) continue;
        bezet.push([lx, ly, br]);
        if (isBol) {
          const k = knopen.find((q) => q.naam === L[3] && (q.soort === "thema" || q.soort === "onderwerp"));
          const n = k?.bol?.punten ?? 0;
          const sub = `${n} punten${k?.bol?.opkomend ? ` · ${k.bol.opkomend} op` : ""}`;
          if (L[2] >= 40) {
            ctx.fillStyle = "#fff";
            if (br > L[2] * 2 - 12 && tekst.includes(" ")) {
              const woorden = tekst.split(" "); const helft = Math.ceil(woorden.length / 2);
              ctx.fillText(woorden.slice(0, helft).join(" "), lx, ly - 7);
              ctx.fillText(woorden.slice(helft).join(" "), lx, ly + 7);
              ctx.font = `500 11px ${font}`; ctx.fillText(sub, lx, ly + 21);
            } else {
              ctx.fillText(tekst, lx, ly);
              ctx.font = `500 11px ${font}`; ctx.fillText(sub, lx, ly + 14);
            }
          } else {
            // te klein voor tekst erin: naam en getal eronder, met halo
            const oy = L[1] + L[2] + 14;
            ctx.font = `600 12px ${font}`; const bw = Math.max(br, ctx.measureText(sub).width);
            ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.fillRect(lx - bw / 2 - 4, oy - 11, bw + 8, 27);
            ctx.fillStyle = L[5]; ctx.fillText(tekst, lx, oy);
            ctx.font = `500 11px ${font}`; ctx.fillStyle = color.faint; ctx.fillText(sub, lx, oy + 13);
          }
        } else {
          ctx.fillStyle = "rgba(255,255,255,0.88)"; ctx.fillRect(lx - br / 2 - 3, ly - 11, br + 6, 14);
          ctx.fillStyle = L[5]; ctx.fillText(tekst, lx, ly);
        }
      }
      // kader (gebied kiezen)
      const k = kaderRef.current;
      if (k) {
        ctx.setLineDash([5, 4]); ctx.lineWidth = 1.2; ctx.fillStyle = alpha(chart.teal, 0.08);
        ctx.fillRect(Math.min(k[0], k[2]), Math.min(k[1], k[3]), Math.abs(k[2] - k[0]), Math.abs(k[3] - k[1]));
        ctx.strokeStyle = chart.teal; ctx.strokeRect(Math.min(k[0], k[2]), Math.min(k[1], k[3]), Math.abs(k[2] - k[0]), Math.abs(k[3] - k[1])); ctx.setLineDash([]);
      }
    };

    // ── de 2×2 ────────────────────────────────────────────────────────
    const matrixPos: [number, number, number][] = [];
    const tekenMatrix = () => {
      matrixPos.length = 0;
      const xAs = weergave === "matrix" ? "moeite" : "urgentie";
      const m = { l: 56, r: 24, t: 34, b: 44 }; const pw = B - m.l - m.r; const ph = H - m.t - m.b;
      const px = (v: number) => m.l + (v / 10) * pw; const py = (v: number) => m.t + ph - ((v - 5) / 5) * ph;
      const xm = xAs === "moeite" ? 6 : 8; const ym = 8;
      ctx.fillStyle = alpha(chart.teal, 0.06);
      if (xAs === "moeite") ctx.fillRect(m.l, m.t, px(xm) - m.l, py(ym) - m.t); else ctx.fillRect(px(xm), m.t, m.l + pw - px(xm), py(ym) - m.t);
      ctx.strokeStyle = alpha(color.faint, 0.4); ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(px(xm), m.t); ctx.lineTo(px(xm), m.t + ph); ctx.moveTo(m.l, py(ym)); ctx.lineTo(m.l + pw, py(ym)); ctx.stroke(); ctx.setLineDash([]);
      ctx.strokeStyle = color.border; ctx.strokeRect(m.l, m.t, pw, ph);
      ctx.font = `500 12px ${font}`; ctx.fillStyle = color.faint; ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
      const kw = xAs === "moeite"
        ? [["Quick wins: veel impact, weinig moeite", m.l + 8, m.t + 16], ["Grote klus: veel impact, veel moeite", px(xm) + 8, m.t + 16], ["Bijvangst", m.l + 8, m.t + ph - 8], ["Laat liggen", px(xm) + 8, m.t + ph - 8]]
        : [["Belangrijk, kan wachten", m.l + 8, m.t + 16], ["Nu doen: veel impact, hoge urgentie", px(xm) + 8, m.t + 16], ["Volgen", m.l + 8, m.t + ph - 8], ["Urgent maar beperkt", px(xm) + 8, m.t + ph - 8]];
      for (const [t, x, y] of kw) ctx.fillText(String(t), Number(x), Number(y));
      ctx.textAlign = "center"; ctx.fillText(`${xAs} →`, m.l + pw / 2, H - 14);
      ctx.save(); ctx.translate(16, m.t + ph / 2); ctx.rotate(-Math.PI / 2); ctx.fillText("impact →", 0, 0); ctx.restore();
      ctx.font = `11px ${font}`;
      for (let v2 = 0; v2 <= 10; v2 += 2) ctx.fillText(String(v2), px(v2), m.t + ph + 14);
      ctx.textAlign = "right"; for (let w = 5; w <= 10; w++) ctx.fillText(String(w), m.l - 6, py(w) + 4);
      const set = afbakening.onderwerp ? new Set(ob.leden.get(afbakening.onderwerp) ?? []) : afbakening.thema ? new Set(ob.themas.find((t) => t.naam === afbakening.thema)?.onderwerpen.flatMap((o) => ob.leden.get(o) ?? []) ?? []) : null;
      const rij: [number, number, string, number][] = [];
      for (let i = 0; i < punten.length; i++) {
        const p = punten[i]!;
        if (staat[i] !== 2 || p.soort === "onderwerp") continue;
        if (set && !set.has(i)) continue;
        const xv = xAs === "moeite" ? p.moeite : p.urgentie; const yv = p.impact;
        if (xv == null || yv == null) continue;
        if (xAs === "moeite" && p.soort !== "vaardigheid") continue;
        const x = px(xv) + ((i * 7919) % 11 - 5) * 0.9; const y = py(Math.max(5, yv)) + ((i * 104729) % 11 - 5) * 0.9;
        let mee = gemarkeerd ? (gemarkeerd.has(p.id) ? 1 : 0.2) : 1;
        if (gekozenIdx >= 0) mee = i === gekozenIdx ? 1 : Math.min(mee, 0.45);
        const bev = !laagAan && isBevestigd(p);
        if (alleenBevestigd && !bev) mee = Math.min(mee, 0.35);
        const mr = i === gekozenIdx ? 7 : 3.2 + Math.sqrt(p.bronnen ?? 0) * 0.7;
        ctx.globalAlpha = mee; ctx.beginPath(); ctx.arc(x, y, mr, 0, 6.2832);
        ctx.fillStyle = puntKleur(p, i); ctx.fill();
        if (bev) { ctx.lineWidth = 2; ctx.strokeStyle = color.accentGreen; ctx.beginPath(); ctx.arc(x, y, mr + 2.8, 0, 6.2832); ctx.stroke(); }
        if (i === gekozenIdx) { ctx.lineWidth = 2; ctx.strokeStyle = color.ink; ctx.stroke(); }
        matrixPos.push([i, x, y]);
        if (i === gekozenIdx || (gemarkeerd && gemarkeerd.has(p.id)) || (p.bronnen ?? 0) >= 6) rij.push([x, y, p.naam, (p.bronnen ?? 0) + (i === gekozenIdx ? 1000 : 0)]);
      }
      ctx.globalAlpha = 1;
      rij.sort((a, b) => b[3] - a[3]);
      ctx.font = `500 11px ${font}`; ctx.textAlign = "center"; const bezet: [number, number, number][] = [];
      for (const L of rij) {
        if (bezet.length >= 40) break;
        const tk = L[2].length > 40 ? `${L[2].slice(0, 38)}…` : L[2]; const br = ctx.measureText(tk).width; const ly = L[1] - 9;
        if (bezet.some((q) => Math.abs(L[0] - q[0]) < (br + q[2]) / 2 + 6 && Math.abs(ly - q[1]) < 13)) continue;
        bezet.push([L[0], ly, br]);
        ctx.fillStyle = "rgba(255,255,255,0.88)"; ctx.fillRect(L[0] - br / 2 - 3, ly - 10, br + 6, 13); ctx.fillStyle = color.body; ctx.fillText(tk, L[0], ly);
      }
    };

    const rustig = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const vlieg = (nx: number, ny: number, nz: number) => {
      if (rustig) { camRef.current = { x: nx, y: ny, z: nz }; teken(); return; }
      const van = { ...camRef.current }; const t0 = performance.now();
      const stap = (nu: number) => {
        const f = Math.min(1, (nu - t0) / 420); const e = f < 0.5 ? 2 * f * f : 1 - (-2 * f + 2) ** 2 / 2;
        camRef.current = { x: van.x + (nx - van.x) * e, y: van.y + (ny - van.y) * e, z: van.z + (nz - van.z) * e };
        teken(); if (f < 1) requestAnimationFrame(stap);
      };
      requestAnimationFrame(stap);
    };
    const pasKnopen = () => {
      const pk = knopen.filter((k) => k.soort !== "bron");
      if (!pk.length) { vlieg(0.5, 0.5, 1); return; }
      if (laag === 4 && weergave === "lagen") { vlieg(0.5, 0.5, 1); return; }
      const isBol = laag <= 2 && weergave === "lagen";
      if (isBol) {
        const x0 = Math.min(...pk.map((k) => k.x - k.r)); const x1 = Math.max(...pk.map((k) => k.x + k.r));
        const y0 = Math.min(...pk.map((k) => k.y - k.r)); const y1 = Math.max(...pk.map((k) => k.y + k.r));
        const br = Math.max(0.1, x1 - x0); const ho = Math.max(0.1, y1 - y0);
        // het doek is breder dan hoog; de schaal volgt de kleinste zijde, dus de hoogte weegt zwaarder
        vlieg((x0 + x1) / 2, (y0 + y1) / 2 + 0.02, Math.max(0.6, Math.min(1.6, 0.86 / Math.max(br * (H / Math.max(1, B)), ho))));
        return;
      }
      const xs = pk.map((k) => k.x).sort((a, b) => a - b); const ys = pk.map((k) => k.y).sort((a, b) => a - b);
      const pc = (a: number[], f: number) => a[Math.min(a.length - 1, Math.floor(a.length * f))] ?? 0.5;
      const x0 = pc(xs, 0.05); const x1 = pc(xs, 0.95); const y0 = pc(ys, 0.05); const y1 = pc(ys, 0.95);
      const br = Math.max(0.08, x1 - x0); const ho = Math.max(0.08, y1 - y0);
      vlieg((x0 + x1) / 2, (y0 + y1) / 2, Math.max(1, Math.min(laag === 3 ? 14 : 1.2, 0.8 / Math.max(br, ho))));
    };

    if (stuur) {
      stuur.current = {
        pas: pasKnopen,
        zoom: (f) => vlieg(camRef.current.x, camRef.current.y, Math.max(0.5, Math.min(80, camRef.current.z * f))),
        vliegNaar: (id, minZoom = 6) => { const k = knopen.find((q) => q.id === id); if (k) vlieg(k.x, k.y, Math.max(camRef.current.z, laag <= 2 ? 1 : minZoom)); },
      };
    }

    const raak = (px: number, py: number): Knoop | null => {
      if (weergave === "matrix" || weergave === "matrix2") {
        let best: Knoop | null = null; let bd = 121;
        for (const [i, x, y] of matrixPos) { const d = (x - px) ** 2 + (y - py) ** 2; if (d < bd) { bd = d; const p = punten[i]!; best = { id: p.id, x: 0, y: 0, r: 0, kleur: "", naam: p.naam, soort: "punt", i }; } }
        return best;
      }
      let best: Knoop | null = null; let bestD = Infinity;
      for (const k of knopen) {
        const x = sx(k.x); const y = sy(k.y);
        const r = (k.soort === "thema" || (k.soort === "onderwerp" && laag === 2) ? k.r * schaal() : k.r) + 6;
        const d = (x - px) ** 2 + (y - py) ** 2;
        if (d <= r * r && d < bestD) { best = k; bestD = d; }
      }
      return best;
    };
    const raakStreep = (px: number, py: number): Streep | null => {
      if (laag !== 4 && laag !== 3) return null;
      let best: Streep | null = null; let bd = 36;
      for (const s of strepen) {
        if (laag === 3 && s.a.id !== zweefRef.current && s.b.id !== zweefRef.current && s.a.id !== gekozen && s.b.id !== gekozen) continue;
        const x0 = sx(s.a.x); const y0 = sy(s.a.y); const x1 = sx(s.b.x); const y1 = sy(s.b.y);
        const dx = x1 - x0; const dy = y1 - y0; const len = dx * dx + dy * dy;
        const t = len ? Math.max(0, Math.min(1, ((px - x0) * dx + (py - y0) * dy) / len)) : 0;
        const d = (x0 + t * dx - px) ** 2 + (y0 + t * dy - py) ** 2;
        if (d < bd && t > 0.1 && t < 0.9) { bd = d; best = s; }
      }
      return best;
    };

    const kaderRef = { current: null as [number, number, number, number] | null };
    let sleept: false | true | "kader" = false; let laatst: [number, number] | null = null; let versleept = 0; let netKader = false;
    const onDown = (ev: PointerEvent) => {
      const b = doek.getBoundingClientRect();
      if ((gebiedModus || ev.shiftKey) && (laag === 3 || weergave === "alles")) { kaderRef.current = [ev.clientX - b.left, ev.clientY - b.top, ev.clientX - b.left, ev.clientY - b.top]; sleept = "kader"; return; }
      sleept = true; versleept = 0; laatst = [ev.clientX, ev.clientY];
    };
    const onMove = (ev: PointerEvent) => {
      const b = doek.getBoundingClientRect(); const px = ev.clientX - b.left; const py = ev.clientY - b.top;
      if (sleept === "kader" && kaderRef.current) { kaderRef.current[2] = px; kaderRef.current[3] = py; teken(); return; }
      if (sleept && laatst) {
        const s = schaal(); versleept += Math.abs(ev.clientX - laatst[0]) + Math.abs(ev.clientY - laatst[1]);
        camRef.current.x -= (ev.clientX - laatst[0]) / s; camRef.current.y -= (ev.clientY - laatst[1]) / s; laatst = [ev.clientX, ev.clientY]; teken(); return;
      }
      const hit = raak(px, py);
      const id = hit?.id ?? null;
      if (id !== zweefRef.current) { zweefRef.current = id; teken(); }
      if (hit) {
        let sub = "";
        if (hit.bol) sub = `${hit.soort} · ${hit.bol.punten} punten · ${hit.bol.vaardigheden} vaardigheden · ${hit.bol.trends} trends${hit.bol.opkomend ? ` · ${hit.bol.opkomend} opkomend` : ""}`;
        else if (hit.bron) sub = `bron · ${hit.bron.uitgeverType ?? hit.bron.uitgever ?? ""} · ${hit.bron.datum ?? ""}${hit.bron.alineaId ? " · klik: lees de alinea" : ""}`;
        else if (hit.i !== undefined) { const p = punten[hit.i]!; sub = `${p.soort} · ${p.bronnen} bron${p.bronnen === 1 ? "" : "nen"}${p.impact != null ? ` · impact ${p.impact}` : ""} · ${bew(hit.i)}${isBevestigd(p) ? " · bevestigd" : ""}`; }
        setZweef({ tekst: hit.naam, sub, x: px, y: py });
        doek.style.cursor = "pointer";
      } else {
        const st = raakStreep(px, py);
        if (st) { setZweef({ tekst: `${st.a.naam} → ${st.b.naam}`, sub: "klik: waarom verbonden", x: px, y: py }); doek.style.cursor = "pointer"; }
        else { setZweef(null); doek.style.cursor = gebiedModus ? "crosshair" : "grab"; }
      }
    };
    const onUp = () => {
      if (sleept === "kader") {
        const k = kaderRef.current; kaderRef.current = null; netKader = true; sleept = false;
        if (k && onGebied) {
          const x0 = Math.min(k[0], k[2]); const x1 = Math.max(k[0], k[2]); const y0 = Math.min(k[1], k[3]); const y1 = Math.max(k[1], k[3]);
          const ids: string[] = [];
          for (const kn of knopen) { if (kn.soort !== "punt") continue; const x = sx(kn.x); const y = sy(kn.y); if (x >= x0 && x <= x1 && y >= y0 && y <= y1 && !kn.dim) ids.push(kn.id); }
          onGebied(ids);
        }
        teken(); return;
      }
      sleept = false; laatst = null;
    };
    const onClick = (ev: MouseEvent) => {
      if (netKader) { netKader = false; return; }
      const wasSlepen = versleept > 6; versleept = 0;
      if (wasSlepen || gebiedModus) return;
      const b = doek.getBoundingClientRect(); const px = ev.clientX - b.left; const py = ev.clientY - b.top;
      const hit = raak(px, py);
      if (hit) {
        if (hit.soort === "thema") onThema(hit.naam);
        else if (hit.soort === "onderwerp") onOnderwerp(hit.id);
        else if (hit.soort === "bron") { if (hit.bron?.alineaId) onAlinea(hit.bron.alineaId, hit.bron.citaat); }
        else if (hit.id !== gekozen) onKies(hit.id);
        return;
      }
      const st = raakStreep(px, py);
      if (st) { onVerband(st.a.id, st.b.id); return; }
      if (weergave === "alles" || laag === 3) onKies(null);
    };
    const onDubbel = (ev: MouseEvent) => {
      const b = doek.getBoundingClientRect(); const s2 = schaal();
      vlieg((ev.clientX - b.left - B / 2) / s2 + camRef.current.x, (ev.clientY - b.top - H / 2) / s2 + camRef.current.y, Math.min(80, camRef.current.z * 2.2));
    };
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      if (weergave === "matrix" || weergave === "matrix2") return;
      const b = doek.getBoundingClientRect();
      const naarWereld = (px: number, py: number): [number, number] => { const s = schaal(); return [(px - B / 2) / s + camRef.current.x, (py - H / 2) / s + camRef.current.y]; };
      const voor = naarWereld(ev.clientX - b.left, ev.clientY - b.top);
      camRef.current.z = Math.max(0.5, Math.min(80, camRef.current.z * Math.exp(-ev.deltaY * 0.0016)));
      const na = naarWereld(ev.clientX - b.left, ev.clientY - b.top);
      camRef.current.x += voor[0] - na[0]; camRef.current.y += voor[1] - na[1];
      teken();
      if (weergave === "alles") onTelling({ punten: knopen.length, lijnen: strepen.length, zoom: camRef.current.z, niveau });
    };
    const onLeave = () => { zweefRef.current = null; setZweef(null); teken(); };

    doek.addEventListener("pointerdown", onDown); doek.addEventListener("pointermove", onMove); doek.addEventListener("pointerup", onUp);
    doek.addEventListener("pointerleave", onLeave); doek.addEventListener("click", onClick); doek.addEventListener("dblclick", onDubbel);
    doek.addEventListener("wheel", onWheel, { passive: false });
    const onResize = () => { meet(); teken(); };
    window.addEventListener("resize", onResize);

    meet();
    if (nieuweLaag) { camRef.current = { x: 0.5, y: 0.5, z: 1 }; teken(); pasKnopen(); }
    else teken();
    // op laag 1 en 2 tellen we punten, niet lidmaatschappen: een punt in twee
    // onderwerpen is één punt
    let geteld = 0;
    if (laag <= 2 && weergave === "lagen") {
      const gezien = new Set<number>();
      for (const k of knopen) {
        const ids = k.soort === "thema" ? (ob.themas.find((t) => t.naam === k.id)?.onderwerpen ?? []) : [k.id];
        for (const ow of ids) for (const i of ob.leden.get(ow) ?? []) if (staat[i] === 2) gezien.add(i);
      }
      geteld = gezien.size;
    } else geteld = knopen.filter((k) => k.soort === "punt" && !k.dim).length;
    onTelling({ punten: geteld, lijnen: strepen.length, zoom: camRef.current.z, niveau });

    return () => {
      doek.removeEventListener("pointerdown", onDown); doek.removeEventListener("pointermove", onMove); doek.removeEventListener("pointerup", onUp);
      doek.removeEventListener("pointerleave", onLeave); doek.removeEventListener("click", onClick); doek.removeEventListener("dblclick", onDubbel);
      doek.removeEventListener("wheel", onWheel); window.removeEventListener("resize", onResize);
    };
  }, [data, ob, staat, afbakening, periode, weergave, kleurModus, grootteModus, gekozen, markering, bronnen, vermoed, alleenBevestigd, laag, onThema, onOnderwerp, onKies, onVerband, onAlinea, onTelling, onGebied, gebiedModus, stuur]);

  return (
    <div style={{ position: "relative" }}>
      <canvas
        ref={doekRef}
        data-graaf
        data-laag={laag}
        data-alleen-bevestigd={alleenBevestigd ? "ja" : undefined}
        aria-label="Lagenkaart van thema's, onderwerpen, trends en vaardigheden"
        style={{ display: "block", width: "100%", height: hoogte, background: color.surfaceAlt, border: `1px solid ${color.line}`, borderRadius: 12, cursor: gebiedModus ? "crosshair" : "grab", touchAction: "none" }}
      />
      <div style={{ position: "absolute", right: 12, bottom: 12, display: "grid", gap: 6, zIndex: 4 }}>
        {[
          { teken: "+", label: "Inzoomen", fn: () => stuur?.current?.zoom(1.6) },
          { teken: "−", label: "Uitzoomen", fn: () => stuur?.current?.zoom(1 / 1.6) },
          { teken: "pas", label: "Passend maken", fn: () => stuur?.current?.pas() },
        ].map((k) => (
          <button key={k.label} type="button" aria-label={k.label} onClick={k.fn}
            style={{ width: 34, height: 34, cursor: "pointer", background: color.surface, color: color.ink, border: `1px solid ${color.border}`, borderRadius: 8, fontSize: k.teken === "pas" ? 11 : 17, fontWeight: 500 }}>
            {k.teken}
          </button>
        ))}
      </div>
      {zweef && (
        <div style={{ position: "absolute", left: zweef.x + 16, top: zweef.y + 16, maxWidth: 320, pointerEvents: "none", background: color.surface, border: `1px solid ${color.line}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, lineHeight: 1.4, boxShadow: "0 6px 20px -10px rgba(0,0,0,0.35)", zIndex: 5 }}>
          <div style={{ color: color.ink }}>{zweef.tekst}</div>
          <div style={{ fontSize: 11, color: color.faint, marginTop: 3 }}>{zweef.sub}</div>
        </div>
      )}
    </div>
  );
}
